import { Checkbox } from '@heidi/design-system/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import { TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { usePatientProfileFilter } from './hooks/usePatientProfileFilter';

/**
 * Inner Command-based patient profile picker — Popover-compatible.
 *
 * `enabled` defaults to `false`: pass a visibility signal so the roster is
 * fetched only when the surface is actually shown (mobile passes `!hidden`,
 * the desktop sub-menus pass `isOpen`). Bare popover usage — the
 * `BaseFilterChip` in `SessionListChipRow` — renders only while a selection
 * exists, so the hook's `selected.length` trigger covers it. The existing
 * `<PatientProfileFilter>` wraps this inside a `DropdownMenuSub` for the
 * sidebar's nested-menu layout and continues to work unchanged.
 */
export const PatientProfileFilterPicker = ({
  enabled = false,
}: { enabled?: boolean } = {}) => {
  const { t } = useTranslations();
  const {
    patientProfiles,
    selectedPatientProfilesFilter,
    searchValue,
    setSearchValue,
    handleFilter,
    handleSelect,
  } = usePatientProfileFilter({ enabled });

  return (
    <Command filter={handleFilter} className="bg-transparent">
      <CommandInput
        value={searchValue}
        onValueChange={setSearchValue}
        placeholder={t('patient.searchForPatient')}
        className="h-9 py-0"
        showClearButton
      />
      <CommandList>
        <CommandEmpty>{t('common.noPatientFound')}</CommandEmpty>
        <CommandGroup>
          {patientProfiles?.map((patientProfile) => (
            <CommandItem
              key={patientProfile.id}
              onSelect={() => {
                if (patientProfile.id) {
                  handleSelect(patientProfile.id);
                }
              }}
              value={patientProfile.id ?? ''}
              className="flex items-center gap-2"
            >
              <Checkbox
                checked={
                  !!patientProfile.id &&
                  selectedPatientProfilesFilter.includes(patientProfile.id)
                }
              />
              <TypographyP2 className="truncate">
                {patientProfile.demographic_string}
              </TypographyP2>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};
